/** Week map: one pin per clock-in (green) and clock-out (dark), numbered by weekday; auto-fits to the pins. */
import React, { useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { parseIso, fmtDay, fmtHours } from './utils';

const hhmm = (s) => (s ? new Date(s).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) : '—');
const pin = (kind, label, dim, off) => L.divIcon({
  className: '',
  iconSize: [30, 30], iconAnchor: [15, 30], popupAnchor: [0, -28],
  html: `<div style="width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${off ? '#e11d48' : kind === 'in' ? '#059669' : '#1f2937'};border:2px solid ${off ? '#fecdd3' : '#fff'};box-shadow:0 2px 6px rgba(0,0,0,.35);opacity:${dim ? 0.45 : 1};display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:#fff;font:700 11px/1 system-ui">${label}</span></div>`,
});

function Fit({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) map.setView(points[0], 15);
    else map.fitBounds(L.latLngBounds(points), { padding: [40, 40], maxZoom: 16 });
  }, [map, points]);
  return null;
}

export function AttendanceMap({ days, selectedDate, onPickDay, sites = [], offSiteM = 20 }) {
  const pins = useMemo(() => {
    const out = [];
    (days || []).forEach((d) => {
      const wd = fmtDay(parseIso(d.date), { weekday: 'short' }).slice(0, 2);
      d.clockins.forEach((c) => {
        if (c.lat != null && c.lng != null) out.push({ kind: 'in', date: d.date, wd, pos: [c.lat, c.lng], at: c.clock_in_at, place: c.place, site: c.site_name || c.location_name, acc: c.accuracy_m, late: c.late_minutes, off: !!c.in_off_site, dist: c.site_distance_m });
        if (c.out_lat != null && c.out_lng != null) out.push({ kind: 'out', date: d.date, wd, pos: [c.out_lat, c.out_lng], at: c.clock_out_at, place: c.out_place, site: c.out_site_name, acc: c.out_accuracy_m, onsite: c.on_site_minutes, off: !!c.out_off_site, dist: c.out_site_distance_m });
      });
    });
    return out;
  }, [days]);
  const points = useMemo(() => pins.map((p) => p.pos).concat(sites.map((s) => [s.lat, s.lng])), [pins, sites]);
  const byDay = useMemo(() => {
    const m = {};
    pins.forEach((p) => { (m[p.date] ||= []).push(p.pos); });
    return m;
  }, [pins]);

  if (!pins.length) {
    return <div className="rounded-2xl bg-white border p-6 text-sm text-slate-500 text-center" data-testid="att-map-empty">No locations recorded this week — pins appear once someone clocks in or out with location on.</div>;
  }
  return (
    <div className="rounded-2xl bg-white border overflow-hidden" data-testid="att-map">
      <div className="px-4 py-2.5 flex items-center gap-3 flex-wrap text-xs border-b">
        <span className="font-bold text-slate-900">Where they've been this week</span>
        <span className="inline-flex items-center gap-1 text-slate-600"><span className="w-3 h-3 rounded-full bg-emerald-600 inline-block" /> Clock in</span>
        <span className="inline-flex items-center gap-1 text-slate-600"><span className="w-3 h-3 rounded-full bg-gray-800 inline-block" /> Clock out</span>
        {sites.length > 0 && <span className="inline-flex items-center gap-1 text-slate-600"><span className="w-3 h-3 rounded-full border-2 border-sky-500 inline-block" /> Work site</span>}
        {pins.some((p) => p.off) && <span className="inline-flex items-center gap-1 text-rose-700 font-semibold" data-testid="att-map-off-site-legend"><span className="w-3 h-3 rounded-full bg-rose-600 inline-block" /> Off site (&gt;{offSiteM} m)</span>}
        <span className="ml-auto text-slate-400" data-testid="att-map-count">{pins.length} pin{pins.length === 1 ? '' : 's'} · tap a pin to jump to that day</span>
      </div>
      <div className="h-[380px]">
        <MapContainer center={points[0]} zoom={13} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false}>
          <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <Fit points={points} />
          {sites.map((s) => <Circle key={s.id} center={[s.lat, s.lng]} radius={s.radius_m || 250} pathOptions={{ color: '#0ea5e9', weight: 2, fillOpacity: 0.08 }} />)}
          {Object.entries(byDay).map(([date, pts]) => pts.length > 1 && <Polyline key={date} positions={pts} pathOptions={{ color: date === selectedDate ? '#1f2937' : '#94a3b8', weight: 2, dashArray: '4 6', opacity: date === selectedDate ? 0.9 : 0.5 }} />)}
          {pins.map((p, i) => (
            <Marker key={i} position={p.pos} icon={pin(p.kind, p.wd, selectedDate && p.date !== selectedDate, p.off)} eventHandlers={{ click: () => onPickDay?.(p.date) }}>
              <Popup>
                <div className="text-xs" data-testid="att-map-popup">
                  <div className="font-bold">{p.kind === 'in' ? 'Clocked in' : 'Clocked out'} · {fmtDay(parseIso(p.date))} {hhmm(p.at)}</div>
                  {p.off && <div className="text-rose-700 font-bold">OFF SITE — {p.dist > 1000 ? `${(p.dist / 1609.344).toFixed(1)} mi` : `${p.dist} m`} from {p.site}</div>}
                  <div>{p.place || `${p.pos[0].toFixed(4)}, ${p.pos[1].toFixed(4)}`}{p.site ? ` · ${p.site}` : ''}{p.acc ? ` (±${Math.round(p.acc)} m)` : ''}</div>
                  {p.kind === 'in' && p.late ? <div className="text-rose-700 font-semibold">{p.late} min late</div> : null}
                  {p.kind === 'out' && p.onsite != null ? <div className="text-slate-600">{fmtHours(p.onsite)} on site</div> : null}
                  <a className="text-sky-700 underline" href={`https://www.google.com/maps?q=${p.pos[0]},${p.pos[1]}`} target="_blank" rel="noreferrer">Open in Google Maps</a>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
